import * as React from "react";
import { RoutingActions } from "../../../../../../react-core/Flux/Variables";
import { HierarchyLevelName } from "../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { AdvancedSearchFilterType } from "../../../../../../Wrap/Constants/Enums/Search/AdvancedSearchFilterType";
import { UserClaim } from "../../../../../../Wrap/Constants/Enums/UserClaim";
import { AccountStatus } from "../../../../../../Wrap/Models/Domains/Account/SlAccounts/AccountStatus";
import { AccountSearchModel } from "../../../../../../Wrap/Models/Models/Transformation/Account/AccountSearchModel";
import { AccountSearchResult } from "../../../../../../Wrap/Models/Models/Transformation/Account/AccountSearchResult";
import { HierarchyIdHelper } from "../../../../../Shared/Common/HierarchyIdHelper";
import { TextAndDots } from "../../../../../Shared/Dropdown/TextAndDots";
import { checkPermission } from "../../../../../Shared/Permission/PermissionHelper";
import { ITableConfig, Table } from "../../../../../Shared/Table/Table";
import { StaticUrls } from "../../../../Resoures/StaticUrls";
import { Urls } from "../../../../Urls";
import { IAdvancedSearchQuery, IBasicSearchQuery } from "../../ClientRecord/Action/AccountSearchAction";
import { accountStatusMap, DimCompanyName } from "../../ClientRecord/Resource/Contents";
import { AccountSearchRegularDetailsPopUp } from "./AccountSearchRegularDetailsPopUp";

interface IAccountSearchResultTableProps {
    data: AccountSearchModel;
    onPageChange: (currentPage, pageSize) => void;
    usingInTools?: (row) => void;
    errorMsg?: string;
    advancedQuery?: Array<IAdvancedSearchQuery>;
    fromToolAndLiterature?: boolean;
    basicQuery: IBasicSearchQuery;
}

export const AccountSearchResultTable = (props: IAccountSearchResultTableProps) => {
    if (!props.data) {return null;}

    const [currentPage, setCurrentPage] = React.useState(1);

    const col: ITableConfig[] = [
        {
            title: "Account name",
            key: "AccountName",
            render: (data, row: AccountSearchResult) => {
                if (props.fromToolAndLiterature) {
                    if (props.usingInTools) {
                        return (
                            <a className="hyperlink" onClick={() => props.usingInTools(row)}>
                                {data}
                            </a>
                        );
                    }
                    return checkPermission(UserClaim.AdviserPortalClientSummaryCanView) ? (
                        <a
                            className="hyperlink"
                            onClick={() =>
                                RoutingActions.navigateTo(
                                    Urls.get()
                                        .main.PortfolioDashboard({
                                            hierarchyId: HierarchyIdHelper.toHierarchyIdString(
                                                HierarchyLevelName.Customer,
                                                row?.CustomerId
                                            ),
                                        })
                                        .summary.getUrl()
                                )
                            }
                        >
                            {data}
                        </a>
                    ) : (
                        data
                    );
                } else {
                    if (row?.AccountType == "Consolidated") {
                        return checkPermission(UserClaim.AdviserPortalClientSummaryCanView) ? (
                            <a
                                className="hyperlink"
                                onClick={() =>
                                    RoutingActions.navigateTo(
                                        Urls.get()
                                            .main.PortfolioDashboard({
                                                hierarchyId: row?.HierarchyId,
                                            })
                                            .summary.getUrl()
                                    )
                                }
                            >
                                {data}
                            </a>
                        ) : (
                            data
                        );
                    } else if (props?.basicQuery?.company == DimCompanyName) {
                        return checkPermission(UserClaim.AdviserPortalClientSummaryCanView) ? (
                            <a
                                className="hyperlink"
                                onClick={() =>
                                    RoutingActions.navigateTo(
                                        Urls.get()
                                            .main.PortfolioDashboard({
                                                hierarchyId: row?.HierarchyId,
                                            })
                                            .summary.getUrl()
                                    )
                                }
                            >
                                {data}
                            </a>
                        ) : (
                            data
                        );
                    } else {
                        return checkPermission(UserClaim.AdviserPortalClientSummaryCanView) ? (
                            <a
                                className="hyperlink"
                                onClick={() =>
                                    RoutingActions.navigateTo(
                                        Urls.get()
                                            .main.PortfolioDashboard({
                                                hierarchyId: HierarchyIdHelper.toHierarchyIdString(
                                                    HierarchyLevelName.Customer,
                                                    row?.CustomerId
                                                ),
                                            })
                                            .summary.getUrl()
                                    )
                                }
                            >
                                {data}
                            </a>
                        ) : (
                            data
                        );
                    }
                }
            },
        },
        { title: "Account number", key: "AccountNumber" },
        {
            title: "Account type",
            key: "AccountType",
            render: (data: string) => {
                return data.toLowerCase() == "individ" ? "Individual" : data;
            },
        },
        { title: "Adviser", key: "AdviserName" },
        {
            title: "Status",
            key: "Status",
            render: (data) => {
                return accountStatusMap[data];
            },
        },
    ];

    if (props.fromToolAndLiterature) {
        col.push({
            title: "Action",
            render: (data, row: AccountSearchResult) => {
                let textLinks = [
                    checkPermission(UserClaim.AdviserPortalAccountTransfersCanView)
                        ? {
                              title: "Transactions",
                              url: StaticUrls.ClientDashboardUrl(row.HierarchyId, "4"),
                          }
                        : null,
                    checkPermission(UserClaim.AdviserPortalDocumentLibraryCanView)
                        ? {
                              title: "Document library",
                              url: StaticUrls.ClientDashboardUrl(row.HierarchyId, "5"),
                          }
                        : null,
                    checkPermission(UserClaim.AdviserPortalClientPortfolioReportReadWrite)
                        ? {
                              title: "Create client report",
                              url: StaticUrls.ClientDashboardUrl(row.HierarchyId),
                          }
                        : null,
                ];
                textLinks = textLinks.filter((x) => x != null);
                return <>{row.Status == AccountStatus.Active && <TextAndDots textLinks={textLinks} />}</>;
            },
        });
    }

    if ((props.advancedQuery || [])?.find((x) => x.filterType == AdvancedSearchFilterType.Wrapper)) {
        col.push({
            title: "Wrapper",
            key: "Wrappers",
        });
    }
    const mandateFilter = (props.advancedQuery || [])?.find((x) => x.filterType == AdvancedSearchFilterType.Mandate);
    if (mandateFilter) {
        if (mandateFilter.investedInMandates) {
            col.push({
                title: "Mandates",
                key: "MandateName",
            });
        } else {
            col.push({
                title: "Mandates",
                render: () => {
                    return "No mandate holdings";
                },
            });
        }
    }

    const regularPaymentFilter = (props.advancedQuery || [])?.find(
        (x) => x.filterType == AdvancedSearchFilterType.RegularPayment
    );
    if (regularPaymentFilter) {
        if (regularPaymentFilter.hasRegularPayments) {
            col.push({
                title: "Regular payment",
                render: (data, row: AccountSearchResult) => {
                    return (
                        <AccountSearchRegularDetailsPopUp
                            headAccountHierarchyId={row?.HierarchyId}
                            displayText={"Yes"}
                            type={"regularPayments"}
                        />
                    );
                },
            });
        } else {
            col.push({
                title: "Regular payment",
                render: () => {
                    return "No";
                },
            });
        }
    }

    const regularWithdrawalFilter = (props.advancedQuery || [])?.find(
        (x) => x.filterType == AdvancedSearchFilterType.RegularWithdrawal
    );
    if (regularWithdrawalFilter) {
        if (regularWithdrawalFilter.hasRegularWithdrawals) {
            col.push({
                title: "Regular withdrawal",
                render: (data, row: AccountSearchResult) => {
                    return (
                        <AccountSearchRegularDetailsPopUp
                            headAccountHierarchyId={row?.HierarchyId}
                            displayText={"Yes"}
                            type={"regularWithdrawals"}
                        />
                    );
                },
            });
        } else {
            col.push({
                title: "Regular withdrawal",
                render: () => {
                    return "No";
                },
            });
        }
    }

    return (
        <Table
            data={props.data.AccountList}
            col={col}
            showEmptyMsg
            emptyMsg={props.errorMsg}
            cssWrapper="mb-60"
            needPagination={{
                totalNumOfResult: props.data.TotalNumberOfResults,
                onPaginationChange: (pageIndex, pageSize) => {
                    setCurrentPage(pageIndex);
                    props.onPageChange(pageIndex, pageSize);
                },
                currentPage: currentPage,
            }}
        />
    );
};
